import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from '@microsoft/signalr';
import { authStore } from '@/auth/authStore';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5001';

/**
 * Opens a SignalR connection to the notifications hub and invalidates the inbox/unread queries
 * whenever a notification is pushed to the signed-in user — so the bell badge and list update live.
 * Mount once (at the app layout) per session.
 */
export function useNotificationsLive() {
  const qc = useQueryClient();

  useEffect(() => {
    if (!authStore.getState().accessToken) return;

    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`${baseURL}/hubs/notifications`, {
        accessTokenFactory: () => authStore.getState().accessToken ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on('notification', () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });
    });

    connection.start().catch(() => { /* offline; the polling fallback keeps the badge fresh */ });

    return () => {
      connection.off('notification');
      if (connection.state !== HubConnectionState.Disconnected) void connection.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
